import React from 'react';
import { CaptureLevel, CollapsedInfo } from '../../../@types/shared';
import { Box, Grid, IconButton, Theme, useTheme } from '@mui/material';
import CompanySelect from '../../shared/input/select/CompanySelect';
import CountrySelect from '../../shared/input/select/CountrySelect';
import { CompanyExtended } from '../../../lib/app/company';
import { Country, Group } from '@esg/esg-global-types';
import { UserContext } from '../../../context/UserContext';
import { getUserGroups, userAllowedCompany } from '../../../util/user_access';
import { ReactComponent as GroupIcon } from '../../../assets/icons/Group.svg';
import { ReactComponent as CompanyIcon } from '../../../assets/icons/Company_Organisation.svg';
import { ReactComponent as LocationIcon } from '../../../assets/icons/Location.svg';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { Location, useLocation } from 'react-router-dom';
import PrimaryButton from '../../shared/button/PrimaryButton';
import PrimaryTabs from '../../shared/tab/PrimaryTabs';
import PrimaryTab from '../../shared/tab/PrimaryTab';
import BorderContainer from '../../shared/container/BorderContainer';
import { MinorHeading, Subheading } from '../../shared/typography/PrimaryTypography';
import PrimarySkeleton from '../../shared/loading/PrimarySkeleton';
import ColorTag from '../../shared/decoration/ColorTag';
import { ReadinessGaugeParameters } from '../../../lib/readiness_gauge/readiness_gauge_instance';
import { GroupContext } from '../../../context/GroupContext';
import { getCompanies } from '../../../graphql/services/company.service';
import { getGroups } from '../../../graphql/services/group.service';

interface ReadinessGaugeParametersProps {
  readiness_gauge_parameters: ReadinessGaugeParameters;
  questionnaire_started: boolean;
  handleParametersChange: (parameters: ReadinessGaugeParameters) => void;
  handleStartQuestionnaire: () => void;
}

/**
 * A collapsable list of inputs that defines the parameters of the readiness gauge questionnaire to be captured.
 * @param {ReadinessGaugeParameters} readiness_gauge_parameters Object of parameter fields to be manipulated in parent.
 * @param {boolean} questionnaire_started Flag for the readiness gauge questionnaire being started.
 * @param {(parameters: ReadinessGaugeParameters): void} handleParametersChange Update the readiness gauge parameters object in parent.
 * @param {(): void} handleStartQuestionnaire Function executed on button click to generate questionnaire based on parameters.
 * @returns {JSX.Element}
 */
const ReadinessGaugeParametersMenu = ({
  readiness_gauge_parameters,
  questionnaire_started,
  handleParametersChange,
  handleStartQuestionnaire
}: ReadinessGaugeParametersProps) => {
  const { user } = React.useContext(UserContext);
  const { group } = React.useContext(GroupContext);
  const [tab, setTab] = React.useState<number>(0);
  const [allowedGroups, setAllowedGroups] = React.useState<Array<Group> | undefined | null>(null);
  const [allowedCompanies, setAllowedCompanies] = React.useState<
    Array<CompanyExtended> | undefined
  >([]);
  const [collapse, setCollapse] = React.useState<boolean>(false);
  const location: Location = useLocation();
  const theme: Theme = useTheme();
  const primary_blue: string = theme.palette.primary.main;

  React.useEffect(() => {
    (async () => {
      if (user) {
        const groups = await getGroups();
        const user_groups = getUserGroups(user);
        setAllowedGroups(
          user.super_admin
            ? groups
            : groups.filter((group) => user_groups.find((user_group) => group.id === user_group))
        );
      }
    })();
  }, [user]);

  React.useEffect(() => {
    (async () => {
      if (allowedGroups && user) {
        const allowed_companies: Array<CompanyExtended> = await getCompanies().then((companies) =>
          companies.filter((company) =>
            allowedGroups.find((group) => group.id === company.group.id)
          )
        );
        setAllowedCompanies(
          allowed_companies.filter((company) => userAllowedCompany(user, company))
        );
      }
    })();
  }, [allowedGroups]);

  React.useEffect(() => {
    (() => {
      if (location.state) {
        setTab(
          location.state.readinessGaugeParameters.country
            ? 2
            : location.state.readinessGaugeParameters.company
              ? 1
              : 0
        );
        setCollapse(true);
      }
    })();
  }, []);

  const collapsed_info: Array<CollapsedInfo> = [];

  if (group) {
    collapsed_info.push({
      id: 'group',
      icon: <GroupIcon height={35} fill={primary_blue} stroke={primary_blue} />,
      value: group.name || 'Group'
    });
  }

  if (readiness_gauge_parameters.company) {
    collapsed_info.push({
      id: 'company',
      icon: <CompanyIcon height={35} fill={primary_blue} stroke={primary_blue} />,
      value: readiness_gauge_parameters.company.name || 'Company'
    });
  }

  if (readiness_gauge_parameters.country) {
    collapsed_info.push({
      id: 'country',
      icon: <LocationIcon height={35} fill={primary_blue} stroke={primary_blue} />,
      value: readiness_gauge_parameters.country.name || 'Country'
    });
  }

  const capture_levels: Array<CaptureLevel> = [
    {
      id: 'group',
      label: 'Group',
      input_fields: ['group'],
      disabled: false
    },
    {
      id: 'company',
      label: 'Company',
      input_fields: ['company'],
      disabled: false
    },
    {
      id: 'national',
      label: 'Country',
      input_fields: ['company', 'country'],
      disabled: false
    }
  ];

  const current_input_fields: Array<string> = capture_levels[tab].input_fields;
  const input_valid: boolean = current_input_fields.every((input_field: string) => {
    return readiness_gauge_parameters[input_field] !== null;
  });

  const handleTabChange = (event: React.SyntheticEvent, new_tab_value: number): void => {
    const new_parameters: ReadinessGaugeParameters = {
      ...readiness_gauge_parameters,
      company: null,
      country: null
    };
    handleParametersChange(new_parameters);
    setTab(new_tab_value);
  };
  return (
    <BorderContainer
      sx={{
        textAlign: 'start',
        my: 2,
        flexGrow: 1
      }}
    >
      <Grid container spacing={4}>
        {collapse ? (
          <>
            <Grid size={11} sx={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
              {collapsed_info
                .filter((input_info: CollapsedInfo) => input_info !== null)
                .map((input_info: CollapsedInfo) => {
                  return (
                    <Box
                      key={input_info.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        border: `1px solid ${primary_blue}`,
                        borderRadius: '5px',
                        gap: 2,
                        py: 1,
                        px: 2,
                        mx: 2,
                        minWidth: '250px',
                        color: primary_blue
                      }}
                    >
                      {input_info.icon}
                      <MinorHeading>{input_info.value}</MinorHeading>
                      {input_info.end_icon}
                    </Box>
                  );
                })}
            </Grid>
            <Grid size={1} sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                sx={{ color: 'black' }}
                onClick={() => {
                  setCollapse(false);
                }}
              >
                <ExpandMore fontSize="large" color="primary" />
              </IconButton>
            </Grid>
          </>
        ) : (
          <>
            <Grid size={9} alignSelf={'center'}>
              <Subheading weight="Medium" id="capture-by-heading">
                Complete Readiness Gauge for
              </Subheading>
            </Grid>
            <Grid size={3} alignSelf={'center'} display={'flex'}>
              <Grid
                container
                spacing={1}
                alignItems={'center'}
                justifyContent={'flex-end'}
                flexGrow={1}
              >
                <Grid justifyContent={'flex-end'}>
                  {group?.enterprise_classification &&
                    'name' in group.enterprise_classification && (
                      <ColorTag
                        text={group.enterprise_classification.name}
                        color={primary_blue}
                        icon={<GroupIcon height={35} fill={primary_blue} stroke={primary_blue} />}
                      />
                    )}
                </Grid>
              </Grid>
            </Grid>
            <Grid size={12}>
              <PrimaryTabs
                value={tab}
                onChange={handleTabChange}
                aria-label="readiness-gauge-parameter-tabs"
                id="readiness-gauge-parameter-tabs"
              >
                {capture_levels.map((capture_level: CaptureLevel, index: number) => {
                  return (
                    <PrimaryTab
                      id={`capture-level-tab-${index}`}
                      onClick={(event) => {
                        if (index === 0) handleTabChange(event, 0);
                      }}
                      key={capture_level.id}
                      label={capture_level.label}
                      value={index}
                      disabled={capture_level.disabled}
                    />
                  );
                })}
              </PrimaryTabs>
            </Grid>
            <Grid size={12}>
              <Grid container spacing={3} sx={{ my: 1 }} id="questionnaire-parameters-select-grid">
                {current_input_fields.map((capture_input: string, index: number) => {
                  return (
                    <Grid
                      key={capture_input}
                      size={2.4}
                      id={`capture-input-grid-item-${index}`}
                      sx={{ display: 'flex', alignItems: 'center' }}
                    >
                      {capture_input === 'group' &&
                        (group ? (
                          <Subheading weight="Medium">{group.name}</Subheading>
                        ) : (
                          <PrimarySkeleton variant="rounded" width={300} height={35} />
                        ))}
                      {capture_input === 'company' && (
                        <CompanySelect
                          options={allowedCompanies?.filter(
                            (company) => company.group?.id === group?.id
                          )}
                          group_id={group?.id}
                          handleChangeCompanies={(company) => {
                            handleParametersChange({
                              ...readiness_gauge_parameters,
                              company: company as CompanyExtended,
                              country: null
                            });
                          }}
                          selected_options={readiness_gauge_parameters.company}
                        />
                      )}
                      {capture_input === 'country' && (
                        <CountrySelect
                          group_id={group?.id}
                          company_id={readiness_gauge_parameters.company?.id}
                          handleChangeCountries={(country) => {
                            handleParametersChange({
                              ...readiness_gauge_parameters,
                              country: country as Country
                            });
                          }}
                          disabled={
                            (!!current_input_fields[index - 1] &&
                              !readiness_gauge_parameters[current_input_fields[index - 1]]) ||
                            capture_levels[tab]?.disabled
                          }
                          selected_options={readiness_gauge_parameters.country}
                          variant="standard"
                        />
                      )}
                    </Grid>
                  );
                })}
                <Grid
                  sx={{
                    marginTop: 1,
                    flexGrow: 1,
                    display: 'flex',
                    justifyContent: 'flex-end'
                  }}
                >
                  {questionnaire_started ? (
                    <IconButton
                      onClick={() => {
                        setCollapse(true);
                      }}
                    >
                      <ExpandLess fontSize="large" color="primary"></ExpandLess>
                    </IconButton>
                  ) : (
                    <PrimaryButton
                      id="start-questionnaire-button"
                      disabled={!input_valid}
                      onClick={() => {
                        handleStartQuestionnaire();
                        setCollapse(true);
                      }}
                    >
                      Start Questionnaire
                    </PrimaryButton>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </>
        )}
      </Grid>
    </BorderContainer>
  );
};

export default ReadinessGaugeParametersMenu;
